import type { SeedRow } from './core';
// Internal platform admin queries (backend §57–82). Separate from user settings on purpose.
import { db, byId, eq, sortBy, update, insert, ORG_ID } from './core';
import { now } from '../lib/format';
import { listRuns, runTotals } from './runs';

const isToday = (iso?: any) => iso && Date.parse(iso) > now().getTime() - 86400000;

// Aggregates for the demo tenant are computed from real rows; other tenants carry precomputed metrics.
export function orgMetrics(org?: any) {
  if (org.id !== ORG_ID) return org.metrics;
  return {
    users: eq('organization_members', 'organization_id', ORG_ID).length,
    workspaces: eq('workspaces', 'organization_id', ORG_ID).length,
    projects: eq('projects', 'organization_id', ORG_ID).length,
    runs_30d: runTotals().runs,
    cost_30d: runTotals().cost,
  };
}

export function overview() {
  const orgs = db.organizations;
  // The window starts on the hour so the list request stays the same between renders.
  const since = new Date(Math.floor(now().getTime() / 3600000) * 3600000 - 86400000).toISOString();
  const runsToday = listRuns({ since, limit: 100 });
  const costToday = db.usage_events.filter((u?: any) => isToday(u.created_at)).reduce((a?: any, u?: any) => a + u.internal_cost, 0);
  const costMonth = db.usage_events.reduce((a?: any, u?: any) => a + u.internal_cost, 0);
  return {
    activeOrganizations: orgs.filter((o?: any) => o.status === 'active').length,
    activeUsers: db.users.filter((u?: any) => u.status === 'active' && !u.is_agent).length,
    runsToday: runsToday.length,
    runningJobs: db.queue_jobs.filter((j?: any) => j.status === 'active').length,
    failedRuns: runTotals().failed,
    queueSize: db.queue_jobs.filter((j?: any) => ['waiting', 'delayed'].includes(j.status)).length,
    providerErrors: db.providers.filter((p?: any) => p.status !== 'healthy').length,
    costToday, costMonth,
  };
}

export const listOrganizations = () => db.organizations.map((o?: any) => ({ ...o, m: orgMetrics(o) }));
export const getOrganization = (id?: any): SeedRow => { const o = byId('organizations', id); return o && { ...o, m: orgMetrics(o) }; };
export const orgUsers = (orgId?: any) => eq('organization_members', 'organization_id', orgId).map((m?: any) => ({ ...m, user: byId('users', m.user_id) }));

export function listUsers() {
  return db.users.map((u?: any) => {
    const mem = db.organization_members.filter((m?: any) => m.user_id === u.id);
    return { ...u, orgs: mem.map((m?: any) => byId('organizations', m.organization_id)), role: mem[0]?.role, runs: runTotals({ userId: u.id }).runs };
  });
}
export const setUserStatus = (id?: any, status?: any) => update('users', id, { status });

export function nodeTypeUsage(typeId?: any) {
  const nodes = eq('nodes', 'node_type_id', typeId);
  return { nodes: nodes.length, flows: new Set(nodes.map((n?: any) => n.flow_id)).size, nodeRuns: eq('node_runs', 'node_type_id', typeId).length, failed: eq('node_runs', 'node_type_id', typeId).filter((r?: any) => r.status === 'failed').length };
}
export const setNodeTypeStatus = (id?: any, status?: any) => update('node_types', id, { status, updated_at: new Date().toISOString() });

export function providerDetail(id?: any) {
  const p = byId('providers', id);
  const nodeRuns = db.node_runs.filter((nr?: any) => nr.provider === p.name);
  return { provider: p, models: eq('models', 'provider_id', id), errors: nodeRuns.filter((nr?: any) => nr.status === 'failed'), calls: nodeRuns.length };
}

export function failedRuns() {
  return listRuns({ status: 'failed' }).map((r?: any) => {
    const nr = db.node_runs.find((x?: any) => x.run_id === r.id && x.status === 'failed');
    return { run: r, flow: byId('flows', r.flow_id), org: byId('organizations', r.organization_id), nodeRun: nr, node: nr && byId('nodes', nr.node_id) };
  });
}

export const queueJobs = (status?: any) => sortBy(db.queue_jobs.filter((j?: any) => !status || j.status === status), 'created_at');
export function queueCounts() {
  const s = ['waiting', 'active', 'delayed', 'failed'];
  const out = Object.fromEntries(s.map((k?: any) => [k, db.queue_jobs.filter((j?: any) => j.status === k).length]));
  out.completed = db.node_runs.filter((nr?: any) => nr.status === 'completed').length;
  return out;
}
export const setJobStatus = (id?: any, status?: any) => update('queue_jobs', id, { status, attempts: (byId('queue_jobs', id).attempts || 0) + (status === 'waiting' ? 1 : 0) });

export function usageBreakdown(by?: any) {
  const groups: Record<string, { key: string; provider_cost: number; internal_cost: number; events: number }> = {};
  db.usage_events.forEach((u?: any) => {
    const key = u[by] || '—';
    groups[key] ||= { key, provider_cost: 0, internal_cost: 0, events: 0 };
    groups[key].provider_cost += u.provider_cost; groups[key].internal_cost += u.internal_cost; groups[key].events += 1;
  });
  return sortBy(Object.values(groups), 'internal_cost');
}

export function dailyCost(days: any = 14) {
  const out = [];
  for (let i = days - 1; i >= 0; i--) {
    const from = now().getTime() - (i + 1) * 86400000, to = now().getTime() - i * 86400000;
    out.push({ day: new Date(to).toISOString(), cost: db.usage_events.filter((u?: any) => { const t = Date.parse(u.created_at); return t > from && t <= to; }).reduce((a?: any, u?: any) => a + u.internal_cost, 0) });
  }
  return out;
}

export const listFlags = () => db.feature_flags;
export const toggleFlag = (id?: any) => { const f = byId('feature_flags', id); update('feature_flags', id, { enabled_globally: !f.enabled_globally, updated_at: new Date().toISOString() }); logAudit('feature_flag.updated', 'feature_flag', id, { enabled_globally: !f.enabled_globally }); };

export const auditEvents = ({ action, entityType }: any = {}) => sortBy(db.audit_events.filter((e?: any) => (!action || e.action === action) && (!entityType || e.entity_type === entityType)), 'created_at');
export const setOrgStatus = (id?: any, status?: any) => { update('organizations', id, { status }); logAudit('organization.status_changed', 'organization', id, { status }); };
export const setProviderStatus = (id?: any, status?: any) => { update('providers', id, { status }); logAudit('provider.status_changed', 'provider', id, { status }); };
export function logAudit(action?: any, entity_type?: any, entity_id?: any, after?: any) {
  insert('audit_events', { id: 'aud_' + Date.now(), organization_id: ORG_ID, user_id: 'usr_platform_admin', action, entity_type, entity_id, before: null, after, created_at: new Date().toISOString() });
}
